import {css, html, HTMLTemplateResult, LitElement, nothing} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  from,
  fromEvent,
  interval,
  map,
  Observable,
  of,
  startWith,
  takeUntil,
} from 'rxjs';

import {Filter, Filterable} from './interfaces/filterable';
import {Panel} from './panel';
import {UnlockableLitElement} from './unlockable-lit-element';
import {elementReady} from './utils';

@customElement('ri-filter-view')
export class FilterView extends Panel {
  // #region Properties (24)

  /** What filters are currently active? */
  private filters$: BehaviorSubject<Set<string>> = new BehaviorSubject(
    new Set()
  );

  public static override styles = [
    ...Panel.styles,
    css`
      .content {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
      }
      .filters,
      .bottom-container {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
        grid-template-rows: masonry;
      }
      .filters * {
        font-size: 0.9rem;
      }
      .bottom-container * {
        /* margin: 2px; */
      }
      .top-container,
      .bottom-container {
        width: auto;
        height: auto;
      }
      .filters,
      .container {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        align-items: flex-start;
      }
      input[type='search'] {
        width: auto;
      }
    `,
  ];

  /**
   * If true, requires an intersection of all filters
   * instead of just a single filter to be true.
   */
  @property({type: Boolean, reflect: true, attribute: 'intersection'})
  public isIntersection = false;
  /**
   * Should the order of the entries be reversed?
   */
  @property({type: Boolean, reflect: true, attribute: 'reversed'})
  public isReversed = false;
  /**
   * Should the entries
   * be replaced with elements of `this.entryTag`?
   */
  @property({type: Boolean}) public replace = false;
  /**
   * If `this.replace`,
   * the entries are created as elements of this tag. */
  @property() public replacementEntryTag = 'div';
  /**
   * Should the filters be hidden?
   */
  @property({type: Boolean, reflect: true}) public showFilters = false;

  public currentFilterables: Filterable[] = [];
  /** A stream of the entries of this FilterView. */
  _filterables$: Observable<Filterable[]> = from([]);
  /** A stream of the currently visible entries of this FilterView. */
  public currentFilterables$!: Observable<Filterable[]>;
  /**
   * A cache for the filters, for performance reasons.
   */
  public filterCache: HTMLTemplateResult[] = [];
  /** Holds the timeout for filterCache, to allow clearing it. */
  public filterCacheTimeout: NodeJS.Timeout | null = null;
  public filterables: Filterable[] = [];
  /** How many filterables must there be before showing the filters? */
  public filterablesBeforeShowFilter = -1;
  /** Are the filterables in a Packery? */
  public isPackery = true;
  /** Any custom options for this view's filterables' Packery. */
  public masonryOptions = {};
  /** The Packery for the filterables, if any. */
  public packery?: Packery;
  /** What filters are currently available? */
  public possibleFilters: Set<Filter> = new Set();
  /** Current search query; subscribed to this.query$*/
  public query = '';
  /** Current search query */
  public query$: Observable<string> = of('');
  /**
   * If `this.replace`,
   * a cache of the replacement elements indexed by filterable name.
   */
  public replacementsCache: Record<string, LitElement> = {};

  public get _currentFilters() {
    // of(...this.possibleFilters).pipe(
    //   filter(
    //     (filter) =>
    //       filter.condition ||
    //       this.filterables.some(
    //         (f) =>
    //           (!('unlocked' in f) || f.unlocked || f.visible) &&
    //           f.tags.has(filter.name)
    //       )
    //   ),
    //   map(
    //     (filter) => html`<input
    //         type="checkbox"
    //         class="filter"
    //         ?checked=${this.filters.has(filter.name)}
    //         id=${filter.name}
    //         value=${filter.name}
    //         @change=${this._onFilterToggle}
    //       /><label class="filter chip ${filter.name}" for=${filter.name}
    //         >${filter.displayName}</label
    //       >`
    //   ),
    //   shareReplay(1)
    // );
    if (this.filterCache.length > 0) return this.filterCache;
    this.filterCache = [...this.possibleFilters]
      .filter(
        (filter) =>
          filter.condition || // TODO TEMP
          this.filterables?.some(
            (f) =>
              (!('unlocked' in f) || f.unlocked || f.visible) &&
              f.tags.has(filter.name)
          )
      )
      .map(
        (filter) => html`<input
            type="checkbox"
            class="filter"
            ?checked=${this.filters.has(filter.name)}
            id=${filter.name}
            value=${filter.name}
            @change=${this._onFilterToggle}
          /><label class="filter chip ${filter.name}" for=${filter.name}
            >${filter.displayName}</label
          >`
      );
    if (this.filterCacheTimeout) clearTimeout(this.filterCacheTimeout);
    this.filterCacheTimeout = setTimeout(() => {
      this.filterCache = [];
    }, 1000);
    return this.filterCache;
  }

  /** A stream of all of entries of this FilterView. */
  public get filterables$(): Observable<Filterable[]> {
    return this._filterables$;
  }

  public set filterables$(value: Observable<Filterable[]>) {
    this._filterables$ = value;
    this.setCurrentFilterablesStream();
  }

  /** What filters are currently active? */
  public get filters(): Set<string> {
    return this.filters$.value;
  }

  public set filters(value: Set<string>) {
    this.filters$.next(value);
  }

  // #endregion Properties (24)

  // #region Constructors (1)

  constructor() {
    super();
    // console.log('constructing', this);
  }

  // #endregion Constructors (1)

  // #region Public Methods (3)

  public compare(_a: Filterable, _b: Filterable) {
    return 0;
  }

  public setCurrentFilterablesStream() {
    // console.log('setting current filterables stream', this);
    this.currentFilterables$ = combineLatest([
      interval(100),
      this.filterables$,
    ]).pipe(
      map(([_, filterables]) => filterables),
      map((filterables) =>
        filterables
          .map((filterable) => {
            if (this.replace) {
              let el: LitElement & {filterable?: Filterable} =
                this.replacementsCache[filterable.name];
              if (!el) {
                el = document.createElement(
                  this.replacementEntryTag
                ) as LitElement & {
                  filterable?: Filterable | undefined;
                };
                el.filterable = filterable;
                el.classList.add(filterable.name.replace(/\s/g, '-'));
                if (el instanceof UnlockableLitElement) {
                  el.setup();
                }
                this.replacementsCache[filterable.name] = el;
              } else {
                el.requestUpdate();
              }
              return el as unknown as Filterable;
            }
            return filterable;
          })
          .sort(this.isReversed ? (a, b) => this.compare(b, a) : this.compare)
      ),
      takeUntil(this.disconnect$)
    );
    this.subscribe('currentFilterables', this.currentFilterables$);
    this.subscribe('filterables', this.filterables$);
    combineLatest([this.filters$, this.currentFilterables$]).subscribe(
      ([filters, filterables]) =>
        filterables.forEach((filterable) => {
          // // BAD
          // if (!this.replace) {
          //   if (!filterable.unlocked) return false;
          //   if (!filterable.visible) return false;
          //   if (filterable.hiddenByPlayer) return false;
          // } else {
          //   if (filterable.hideEntry) return false;
          // }
          // if (filters.size === 0) return true;
          let include = this.isIntersection; // BAD and more often
          for (const filter of filters) {
            // Skip any special filters
            if (
              [...this.possibleFilters].find((f) => f.name === filter)
                ?.condition
            )
              continue;
            if (this.isIntersection) {
              if (!filterable.tags.has(filter)) {
                include = false;
                break;
              }
            } else {
              if (filterable.tags.has(filter)) {
                include = true;
                break;
              }
            }
          }
          for (const filter of this.possibleFilters) {
            if (filter.condition) {
              if (filter.only) {
                if (this.filters.has(filter.name)) {
                  include &&= filter.condition(filterable);
                }
                continue;
              }
              if (
                !this.filters.has(filter.name) &&
                filter.condition(filterable)
              ) {
                include = false;
                break;
              }
            }
          }
          filterable.hiddenByFilter = !include;
          if (include && this.query) {
            let checkFilterable = filterable;
            if (this.replace) {
              checkFilterable = filterable.filterable;
            }
            filterable.hiddenByFilter = !(
              checkFilterable.name.toLowerCase().match(this.query) ||
              checkFilterable.displayName.toLowerCase().match(this.query) ||
              checkFilterable.description.toLowerCase().match(this.query) ||
              checkFilterable.conditionString?.toLowerCase()?.match(this.query)
            );
          }
        })
    );
  }

  public override setup() {
    super.setup();
    this.query$ = fromEvent(
      this.shadowRoot!.getElementById('query')!,
      'input'
    ).pipe(
      map((e) => e.target?.value?.toLowerCase() ?? ''),
      distinctUntilChanged()
    );
    this.setCurrentFilterablesStream();
    this.subscribe('query', this.query$);
    if (this.isPackery) {
      from(elementReady('.bottom-container', this.shadowRoot!)).subscribe(
        (bottomContainer) => {
          this.packery = new Packery(bottomContainer, {
            itemSelector: this.replacementEntryTag,
            horizontalOrder: true,
            columnWidth: 150,
            ...this.masonryOptions,
          });
        }
      );
      // Update the packery when the contents change
      this.filterables$.pipe(startWith()).subscribe(() => {
        this.packery?.reloadItems();
        this.packery?.layout();
      });
    }
    // Should we show the filters bar? Yes if we need to see hidden stuff or have lots of stuff
    this.filterables$
      .pipe(
        map(
          (filterables) =>
            filterables.some((f) => f.hiddenByPlayer) ||
            filterables.filter((f) => f.unlocked && f.visible).length >
              this.filterablesBeforeShowFilter
        ),
        distinctUntilChanged()
      )
      .subscribe((value) => (this.showFilters = value));
  }

  // #endregion Public Methods (3)

  // #region Protected Methods (2)

  protected _onFilterToggle(e: Event) {
    if (!(e.target instanceof HTMLInputElement)) return;
    if (e.target.checked) {
      if (e.target.value) this.filters.add(e.target.value);
    } else {
      this.filters.delete(e.target.value);
    }
    this.requestUpdate();
  }

  // TODO Figure out a way to use the super's render so I don't have to dupe changes
  // Encapsulating the unique bits in a property fails to update
  protected override render() {
    // console.log('rendering', this);
    const content = html`<div class="content">
      <div class="top-container">
        <div>
          🔍
          <input id="query" type="search" placeholder="Search..." size="20" />
        </div>
        ${this.showFilters
          ? html`
              <div class="filter-view-options">
                <input
                  type="checkbox"
                  id="intersection"
                  ?checked=${this.isIntersection}
                  @change=${(event: Event) => {
                    this.isIntersection = (
                      event.target as HTMLInputElement
                    ).checked;
                    this.requestUpdate();
                  }}
                />
                <!-- <label class="chip" for="intersection" >${this
                  .isIntersection
                  ? 'AND Filters'
                  : 'OR Filters'}</label > -->
              </div>
            `
          : nothing}
        <hr />
        ${this.showFilters
          ? html` <div class="filters">
                <!-- <strong>Filters: </strong> -->
                ${this._currentFilters}
              </div>
              <hr />`
          : nothing}
      </div>
      <div class="bottom-container">${this.currentFilterables}</div>
    </div>`;
    return super.render(content);
  }

  // #endregion Protected Methods (2)
}
